import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuEllipsis, LuHouse, LuList, LuPlus, LuSparkles } from 'react-icons/lu';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import useReviewCount from '../../hooks/useReviewCount';
import { usePhoneShell } from './PhoneShellContext';

const BAR_HEIGHT = 49;

// Dispatched when the active Ask tab is tapped again: /chat scrolls its own
// message list, not the window.
export const SCROLL_TOP_EVENT = 'phone:scrolltop';

function Tab({ to, icon, label, active, onClick, badge, ...rest }) {
  return (
    <Box
      as={to ? Link : 'button'}
      to={to}
      type={to ? undefined : 'button'}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      gap="3px"
      pt="7px"
      flex="1"
      minW="64px"
      h={`${BAR_HEIGHT}px`}
      position="relative"
      color={active ? 'phone.tabActive' : 'phone.muted'}
      sx={{ WebkitTapHighlightColor: 'transparent' }}
      {...rest}
    >
      <Box as={icon} boxSize="24px" strokeWidth={1.8} />
      <Text as="span" textStyle="tabLabel" whiteSpace="nowrap">{label}</Text>
      {badge > 0 && (
        <Box
          as="span"
          position="absolute"
          top="4px"
          left="calc(50% + 6px)"
          minW="18px"
          h="18px"
          px="5px"
          borderRadius="9px"
          bg="phone.badgeBg"
          color="phone.badgeFg"
          fontSize="11px"
          fontWeight="700"
          lineHeight="14px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="2px solid"
          borderColor="redesign.bg.surface"
        >
          {badge > 99 ? '99+' : badge}
        </Box>
      )}
    </Box>
  );
}

/**
 * The phone's bottom tab bar: Home, Movements, the raised + (new movement),
 * Ask and More. The + opens quick add without changing the route, and More
 * opens a sheet. Tapping the active tab again scrolls back to the top. The
 * bar hides while the keyboard is up.
 */
export default function PhoneTabBar() {
  const { t } = useTranslation('navigation');
  const { pathname } = useLocation();
  const { can } = useWorkspace();
  const { openQuickAdd, openMore } = usePhoneShell();
  const { keyboardUp } = useKeyboardInset();
  const { fixable } = useReviewCount();

  if (keyboardUp) return null;

  const isActive = (path) => pathname === path || pathname.startsWith(`${path}/`);
  const reTap = (path) => (event) => {
    if (!isActive(path)) return;
    event.preventDefault();
    if (path === '/chat') window.dispatchEvent(new CustomEvent(SCROLL_TOP_EVENT));
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      as="nav"
      aria-label={t('tabs.label')}
      position="fixed"
      left={0}
      right={0}
      bottom={0}
      zIndex="sticky"
      bg="redesign.bg.surface"
      // The hairline is drawn inside the 49px: a border would make it 50.
      boxShadow="inset 0 1px 0 var(--chakra-colors-redesign-border)"
      pb="env(safe-area-inset-bottom)"
      pl="env(safe-area-inset-left)"
      pr="env(safe-area-inset-right)"
    >
      {/* Every slot shares the width, so the whole bar answers a tap: 78px
          slots at 390, 64px at 320. */}
      <Box display="flex" h={`${BAR_HEIGHT}px`}>
        <Tab to="/dashboard" icon={LuHouse} label={t('tabs.home')} active={isActive('/dashboard')} onClick={reTap('/dashboard')} badge={fixable} />
        <Tab to="/movements" icon={LuList} label={t('tabs.movements')} active={isActive('/movements')} onClick={reTap('/movements')} />
        {can('movement.create') && (
          <Box
            as="button"
            type="button"
            aria-label={t('tabs.add')}
            // Synchronous on purpose: iOS raises the keypad only for a focus
            // made inside this tap.
            onClick={() => openQuickAdd()}
            display="flex"
            justifyContent="center"
            flex="1"
            minW="64px"
            h={`${BAR_HEIGHT}px`}
            sx={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Box
              as="span"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="56px"
              h="56px"
              mt="-16px"
              borderRadius="full"
              bg="redesign.accent.fill"
              color="white"
              boxShadow="0 4px 12px rgba(37, 99, 235, 0.35), 0 0 0 4px var(--chakra-colors-redesign-bg-surface)"
              _active={{ transform: 'scale(0.96)' }}
            >
              <Box as={LuPlus} boxSize="28px" strokeWidth={2.2} />
            </Box>
          </Box>
        )}
        <Tab to="/chat" icon={LuSparkles} label={t('tabs.ask')} active={isActive('/chat')} onClick={reTap('/chat')} />
        <Tab icon={LuEllipsis} label={t('tabs.more')} onClick={openMore} aria-haspopup="dialog" />
      </Box>
    </Box>
  );
}
